import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useToastContext } from '../App';

function ServerList() {
  const [servers, setServers] = useState([]);
  const [modpacks, setModpacks] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createLog, setCreateLog] = useState([]);
  const [createProgress, setCreateProgress] = useState(null);
  const addToast = useToastContext();

  const [form, setForm] = useState({
    name: '',
    modpackId: '',
    port: '',
    minRam: '2G',
    maxRam: '4G',
    loader: 'vanilla',
    mcVersion: '',
    loaderVersion: '',
    javaPath: 'java',
    autoRestart: false,
    jvmArgs: '',
  });
  const [versions, setVersions] = useState(null);

  useEffect(() => {
    loadServers();
    loadModpacks();
    const interval = setInterval(loadServers, 5000);
    return () => clearInterval(interval);
  }, []);

  async function loadServers() {
    try {
      const data = await api.getServers();
      setServers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadModpacks() {
    try {
      const data = await api.getModpacks();
      setModpacks(data.filter(p => p.type === 'curseforge'));
    } catch (e) {
      console.error(e);
    }
  }

  async function openCreateModal() {
    setShowCreate(true);
    setCreateLog([]);
    setCreateProgress(null);
    // Fetch next available port + versions in parallel
    try {
      const [portRes, versionsRes] = await Promise.all([
        api.getNextPort().catch(() => ({ port: 30001 })),
        api.getVersions().catch(() => null),
      ]);
      setForm(prev => ({ ...prev, port: String(portRes.port) }));
      if (versionsRes) {
        setVersions(versionsRes);
        // Pre-select latest vanilla version
        if (versionsRes.vanilla?.length > 0) {
          setForm(prev => ({ ...prev, mcVersion: versionsRes.vanilla[0].id }));
        }
      }
    } catch (e) {
      setForm(prev => ({ ...prev, port: '30001' }));
    }
  }

  function handleModpackChange(modpackId) {
    const pack = modpacks.find(p => p.id === modpackId);
    if (pack) {
      // Auto-fill settings from modpack
      let maxRam = '4G';
      if (pack.recommendedRam) {
        const mb = pack.recommendedRam;
        maxRam = mb >= 1024 ? `${Math.ceil(mb / 1024)}G` : `${mb}M`;
      }
      setForm(prev => ({
        ...prev,
        modpackId,
        name: prev.name || pack.name,
        maxRam,
      }));
    } else {
      setForm(prev => ({ ...prev, modpackId }));
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (creating) return;

    const selectedModpack = form.modpackId;

    if (selectedModpack) {
      // Create server with modpack (SSE streaming)
      setCreating(true);
      setCreateLog([]);
      setCreateProgress(null);

      try {
        await api.createServerWithModpack({
          name: form.name,
          modpackId: form.modpackId,
          minRam: form.minRam,
          maxRam: form.maxRam,
          javaPath: form.javaPath || 'java',
          autoRestart: form.autoRestart,
          jvmArgs: form.jvmArgs,
        }, (event) => {
          setCreateLog(prev => [...prev, event]);
          if (event.step === 'done') {
            addToast(`Server "${form.name}" erstellt!`, 'success');
            loadServers();
          }
          if (event.step === 'error') {
            addToast(event.message, 'error');
          }
        });
      } catch (e) {
        addToast(e.message, 'error');
      } finally {
        setCreating(false);
      }
    } else {
      // Regular server creation (no modpack) — with version selection
      try {
        const loaderVersion = form.loader === 'neoforge'
          ? (form.loaderVersion || versions?.neoforge?.[form.mcVersion]?.[0] || '')
          : '';
        await api.createServer({
          name: form.name,
          port: parseInt(form.port),
          minRam: form.minRam,
          maxRam: form.maxRam,
          javaPath: form.javaPath || 'java',
          autoRestart: form.autoRestart,
          jvmArgs: form.jvmArgs,
          loader: form.loader,
          mcVersion: form.mcVersion,
          loaderVersion,
        });
        addToast('Server erstellt!', 'success');
        setShowCreate(false);
        resetForm();
        loadServers();
      } catch (e) {
        addToast(e.message, 'error');
      }
    }
  }

  function resetForm() {
    setForm({ name: '', modpackId: '', port: '', minRam: '2G', maxRam: '4G', loader: 'vanilla', mcVersion: versions?.vanilla?.[0]?.id || '', loaderVersion: '', javaPath: 'java', autoRestart: false, jvmArgs: '' });
    setCreateLog([]);
    setCreateProgress(null);
  }

  function closeModal() {
    if (!creating) {
      setShowCreate(false);
      resetForm();
    }
  }

  async function handleStart(id, e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.startServer(id);
      addToast('Server wird gestartet...', 'info');
      loadServers();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleStop(id, e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.stopServer(id);
      addToast('Server wird gestoppt...', 'info');
      loadServers();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleDelete(id, name, e) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Server "${name}" wirklich loeschen? Alle Server-Dateien werden geloescht!`)) return;
    try {
      await api.deleteServer(id);
      addToast('Server geloescht', 'success');
      loadServers();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  if (loading) {
    return <div className="empty-state"><div className="spinner" /></div>;
  }

  const selectedPack = modpacks.find(p => p.id === form.modpackId);

  return (
    <div>
      <div className="page-header">
        <h2>Server-Verwaltung</h2>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Neuer Server
        </button>
      </div>

      {servers.length === 0 ? (
        <div className="empty-state">
          <div className="icon">&#127981;</div>
          <p>Keine Server vorhanden</p>
          <button className="btn btn-primary" onClick={openCreateModal}>
            Ersten Server erstellen
          </button>
        </div>
      ) : (
        <div className="grid grid-2">
          {servers.map(server => {
            const pack = modpacks.find(p => p.id === server.modpackId);
            return (
              <Link key={server.id} to={`/servers/${server.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="server-card">
                  <div className="flex items-center justify-between">
                    <span className="server-name">{server.name}</span>
                    <span className={`status-badge ${server.status}`}>
                      <span className="status-dot" />
                      {server.status}
                    </span>
                  </div>
                  <div className="server-meta">
                    <span>Port: {server.port}</span>
                    <span>RAM: {server.minRam} - {server.maxRam}</span>
                    <span>Spieler: {server.players?.length || 0}</span>
                  </div>
                  {pack && (
                    <div className="server-meta" style={{ marginTop: 4 }}>
                      <span style={{ color: 'var(--accent)' }}>Modpack: {pack.name}</span>
                    </div>
                  )}
                  <div className="server-actions">
                    {server.status === 'stopped' ? (
                      <button className="btn btn-primary btn-sm" onClick={(e) => handleStart(server.id, e)}>
                        Starten
                      </button>
                    ) : server.status === 'running' ? (
                      <button className="btn btn-danger btn-sm" onClick={(e) => handleStop(server.id, e)}>
                        Stoppen
                      </button>
                    ) : (
                      <button className="btn btn-secondary btn-sm" disabled>
                        {server.status}...
                      </button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={(e) => handleDelete(server.id, server.name, e)}>
                      Loeschen
                    </button>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: creating ? 700 : 520 }}>
            <h3>Neuen Server erstellen</h3>

            {!creating ? (
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <label>Modpack (optional)</label>
                  <select
                    className="form-select"
                    value={form.modpackId}
                    onChange={e => handleModpackChange(e.target.value)}
                  >
                    <option value="">-- Kein Modpack (Vanilla/manuell) --</option>
                    {modpacks.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} v{p.version} (MC {p.mcVersion}, {p.loaderType})
                      </option>
                    ))}
                  </select>
                  {modpacks.length === 0 && (
                    <span className="text-sm text-muted">
                      Noch keine Modpacks importiert. Gehe zu Modpacks &gt; Importieren um eines hinzuzufuegen.
                    </span>
                  )}
                </div>

                {selectedPack && (
                  <div style={{
                    background: 'var(--bg-tertiary)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 16,
                    fontSize: 13,
                  }}>
                    <div className="fw-bold" style={{ marginBottom: 4 }}>{selectedPack.name} v{selectedPack.version}</div>
                    <div className="text-sm text-muted">
                      MC {selectedPack.mcVersion} | {selectedPack.loaderType} {selectedPack.loaderVersion} | {selectedPack.mods?.length || 0} Mods
                    </div>
                    <div className="text-sm text-muted mt-2">
                      Der Server wird automatisch mit dem passenden Mod-Loader, allen Mods und Configs installiert.
                      Port wird automatisch vergeben.
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Server Name *</label>
                  <input
                    className="form-input"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder={selectedPack ? selectedPack.name : 'Mein Minecraft Server'}
                    required
                  />
                </div>

                {!form.modpackId && (
                  <>
                    <div className="form-group">
                      <label>Server-Typ</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {['vanilla', 'neoforge'].map(l => (
                          <button key={l} type="button"
                            onClick={() => setForm(prev => ({ ...prev, loader: l, loaderVersion: '' }))}
                            className={`btn btn-sm ${form.loader === l ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            {l === 'vanilla' ? 'Vanilla' : 'NeoForge'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Minecraft Version</label>
                      {versions ? (
                        <select className="form-select" value={form.mcVersion}
                          onChange={e => setForm(prev => ({ ...prev, mcVersion: e.target.value, loaderVersion: '' }))}
                        >
                          <option value="">-- Version waehlen --</option>
                          {form.loader === 'vanilla'
                            ? versions.vanilla?.slice(0, 30).map(v => (
                                <option key={v.id} value={v.id}>{v.id}</option>
                              ))
                            : Object.keys(versions.neoforge || {})
                                .sort((a, b) => {
                                  const pa = a.split('.').map(Number);
                                  const pb = b.split('.').map(Number);
                                  for (let i = 0; i < 3; i++) { if ((pb[i]||0) !== (pa[i]||0)) return (pb[i]||0) - (pa[i]||0); }
                                  return 0;
                                })
                                .map(mc => (
                                  <option key={mc} value={mc}>{mc}</option>
                                ))
                          }
                        </select>
                      ) : (
                        <span className="text-sm text-muted">Versionen werden geladen...</span>
                      )}
                    </div>

                    {form.loader === 'neoforge' && form.mcVersion && versions?.neoforge?.[form.mcVersion] && (
                      <div className="form-group">
                        <label>NeoForge Version</label>
                        <select className="form-select" value={form.loaderVersion}
                          onChange={e => setForm(prev => ({ ...prev, loaderVersion: e.target.value }))}
                        >
                          <option value="">-- Neueste ({versions.neoforge[form.mcVersion][0]}) --</option>
                          {versions.neoforge[form.mcVersion].map(v => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>Port</label>
                        <input
                          className="form-input"
                          type="number"
                          value={form.port}
                          onChange={e => setForm({ ...form, port: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Java Pfad</label>
                        <input
                          className="form-input"
                          value={form.javaPath}
                          onChange={e => setForm({ ...form, javaPath: e.target.value })}
                          placeholder="java"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Min RAM</label>
                    <input
                      className="form-input"
                      value={form.minRam}
                      onChange={e => setForm({ ...form, minRam: e.target.value })}
                      placeholder="2G"
                    />
                  </div>
                  <div className="form-group">
                    <label>Max RAM</label>
                    <input
                      className="form-input"
                      value={form.maxRam}
                      onChange={e => setForm({ ...form, maxRam: e.target.value })}
                      placeholder="4G"
                    />
                  </div>
                </div>

                {!form.modpackId && (
                  <div className="form-group">
                    <label>JVM Argumente (optional)</label>
                    <input
                      className="form-input"
                      value={form.jvmArgs}
                      onChange={e => setForm({ ...form, jvmArgs: e.target.value })}
                      placeholder="-XX:+UseG1GC -XX:+ParallelRefProcEnabled"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={form.autoRestart}
                      onChange={e => setForm({ ...form, autoRestart: e.target.checked })}
                    />
                    Auto-Restart bei Crash
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {form.modpackId ? 'Server mit Modpack erstellen' : 'Server erstellen'}
                  </button>
                </div>
              </form>
            ) : (
              /* Creating in progress - show log */
              <div>
                <div style={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  lineHeight: 1.6,
                  padding: '12px 16px',
                  background: 'var(--bg-primary)',
                  borderRadius: 8,
                  marginBottom: 16,
                }}>
                  {createLog.length === 0 && (
                    <div className="text-muted">Starte Server-Erstellung...</div>
                  )}
                  {createLog.map((entry, i) => (
                    <div key={i} style={{
                      color: entry.step === 'error' ? 'var(--danger)' :
                             entry.step === 'done' ? 'var(--accent)' :
                             entry.step === 'bluemap' ? 'var(--info)' :
                             'var(--text-secondary)',
                    }}>
                      {entry.message}
                    </div>
                  ))}
                </div>

                {createLog.some(e => e.step === 'done' || e.step === 'error') ? (
                  <div className="modal-actions">
                    <button className="btn btn-primary" onClick={closeModal}>
                      Schliessen
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="spinner" />
                    <span className="text-muted">Server wird erstellt... Dies kann einige Minuten dauern.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ServerList;
